<template>
  <FormCustomFields
    v-model:field-values="localFieldValues"
    :loading="loading"
    :fields="fields"
    :errors="errors"
    :force-enable-inputs="true"
  />
</template>

<script setup lang="ts">
import { watch, computed, ref } from 'vue'
import { CustomFieldTypeValueInput, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { FieldableFormCustomFieldFragment } from '@/components/Fieldable/graphql/fragments'
import useSupplierSharedCustomFields from '@/components/Fieldable/composables/useSupplierSharedCustomFields'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'
import { FragmentType, useFragment } from '@/gql'
import FormCustomFields from '@/components/Fieldable/FormCustomFields.vue'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'

const emits = defineEmits(['update:field-values'])

interface Props {
  accountId: string
  supplierId: string
  fieldableType: TypeSupportingCustomFieldValues
  fieldValues: CustomFieldTypeValueInput[]
  fieldableId?: string
  shouldDisplayErrors?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  fieldableId: undefined,
  shouldDisplayErrors: false,
})

const { fields, loading } = useSupplierSharedCustomFields(
  props.accountId,
  ref(props.supplierId),
  [props.fieldableType],
  PER_PAGE_MAXIMUM,
)

const fieldableFormStore = useFieldableFormStore()

const localFieldValues = computed<CustomFieldTypeValueInput[]>({
  get: () => props.fieldValues,
  set: (newFieldValues) => {
    emits('update:field-values', newFieldValues)
  },
})

const updateFieldsOnStore = (
  newFieldsForStore: FragmentType<typeof FieldableFormCustomFieldFragment>[],
) => {
  fieldableFormStore.updateFields(
    newFieldsForStore.map((field) => useFragment(FieldableFormCustomFieldFragment, field)),
  )
}

watch(fields, (newFields) => {
  updateFieldsOnStore(newFields)
})

watch(
  () => props.fieldValues,
  (newFieldValues) => {
    fieldableFormStore.updateFieldValues(newFieldValues)
  },
  { immediate: true },
)

const errors = computed(() => (props.shouldDisplayErrors ? fieldableFormStore.errors : {}))
</script>
